import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CalendarDays,
  ArrowLeft,
  MapPin,
  Ticket,
  Navigation,
} from "lucide-react";
import { API_URL, LOCAL_URL } from "../constants";
import {
  DownloadAppCard,
  PageError,
  PageLoading,
  PublicShell,
  Avatar,
  imageFallback,
} from "../components/PublicPage";
import PageMetadata from "../components/PageMetadata";
import { useOpenInApp } from "../helpers/deep-link-wrapper";

const api = process.env.NODE_ENV === "production" ? API_URL : LOCAL_URL;
const date = (value: string) =>
  new Date(value).toLocaleDateString("en", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
const time = (value: string) =>
  new Date(value).toLocaleTimeString("en", {
    hour: "numeric",
    minute: "2-digit",
  });

export default function Event() {
  const { id, slug } = useParams();
  const [event, setEvent] = useState<any>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const eventPath = slug ? `event/slug/${encodeURIComponent(slug)}` : `event/${id}`;
    fetch(`${api}/website/${eventPath}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setEvent)
      .catch(() => setFailed(true));
  }, [id, slug]);
  useOpenInApp(event?.slug ? `e/${encodeURIComponent(event.slug)}` : undefined);

  if (failed)
    return <PageError message="This event is unavailable or private." />;

  if (!event) return <PageLoading />;

  const mapUrl =
    event.location?.latitude && event.location?.longitude
      ? `https://maps.google.com/?q=${event.location.latitude},${event.location.longitude}`
      : `https://maps.google.com/?q=${encodeURIComponent(event.location?.address || event.location?.name || event.name)}`;
  const mapQuery =
    event.location?.latitude && event.location?.longitude
      ? `${event.location.latitude},${event.location.longitude}`
      : event.location?.address || event.location?.name || event.name;
  const organizer =
    event.postedBy?.provider || event.postedBy?.user || event.postedBy?.admin;
  const organizerName =
    event.postedBy?.provider?.name ||
    `${event.postedBy?.user?.firstName || ""} ${event.postedBy?.user?.lastName || ""}`.trim() ||
    event.postedBy?.admin?.name;
  const lowestTicket = event.tickets?.length
    ? Math.min(...event.tickets.map((ticket: any) => Number(ticket.price)))
    : null;
  const isCityMoshiTicketed = Boolean(event.sell_tickets_through_citymoshi);
  const ticketDestination = isCityMoshiTicketed
    ? `https://events.citymoshi.com/${event.id}`
    : event.ticket_url;
  const ticketPrice = lowestTicket === null ? "See event details" : lowestTicket === 0 ? "Free" : `${event.tickets[0].currency} ${lowestTicket.toLocaleString()}`;
  const ticketLabel = ticketDestination ? (isCityMoshiTicketed ? "View on CityMoshi Events" : "Get tickets") : "Get the app";
  const ticketButton = (className: string) => <a className={className} href={ticketDestination || "#download"} target={ticketDestination ? "_blank" : undefined} rel={ticketDestination ? "noreferrer" : undefined}><Ticket />{ticketLabel}</a>;
  return <PublicShell className="page-event">
    <PageMetadata title={event.name} description={event.description} image={event.image_original || event.image} url={`/e/${event.slug}`} />
    <main className="detail-main">
      <div className="wrap">
        <section className="ev-hero" aria-label="Event poster">
          <div className="ev-hero__media">
            <img className="ev-hero__bg" src={event.image || imageFallback} alt="" aria-hidden="true" />
            <img className="ev-hero__img" src={event.image_original || event.image || imageFallback} alt={event.name} />
            <Link className="icon-btn ev-hero__back" to="/#discover" aria-label="Back to events"><ArrowLeft /></Link>
            <div className="ev-hero__pills"><span className="pill">{event.category?.name || "Event"}</span></div>
          </div>
        </section>
        <header className="ev-head">
          <h1 className="ev-title">{event.name}</h1>
          {event.subTitle && <p className="ev-sub">{event.subTitle}</p>}
          <div className="ev-head__facts">
            <div className="ev-fact"><span className="detail-fact-icon"><CalendarDays /></span><div><b>{date(event.startDate)}</b><span>{time(event.startDate)}{event.endTime && ` – ${time(event.endTime)}`}</span></div></div>
            <div className="ev-fact"><span className="detail-fact-icon"><MapPin /></span><div><b>{event.location?.name || "Location to be announced"}</b><span>{event.location?.address}</span></div><a className="mini" href={mapUrl} target="_blank" rel="noreferrer">Directions</a></div>
          </div>
        </header>
        <div className="ev-layout">
          <div className="ev-main">
            <section className="ev-sec"><h2 className="ev-h">About this event</h2><p className="ev-desc">{event.description}</p></section>
            {event.tickets?.length > 0 && <section className="ev-sec"><h2 className="ev-h"><Ticket />Tickets</h2><div className="ev-tiers">{event.tickets.map((ticket: any, index: number) => <div className="tier" key={`${ticket.name}-${index}`}><span>{ticket.name}</span><b>{Number(ticket.price) === 0 ? "Free" : `${ticket.currency} ${Number(ticket.price).toLocaleString()}`}</b></div>)}</div>{event.ticket_info && <p className="detail-note">{event.ticket_info}</p>}</section>}
            {organizer && organizerName && <section className="ev-sec"><h2 className="ev-h">Organizer</h2><div className="ev-org"><Avatar src={organizer.picture} name={organizerName} /><div className="ev-org__text"><b>{organizerName}</b><span>Event organizer</span></div><a className="btn btn--ghost btn--sm" href="#download">Follow in app</a></div></section>}
            {event.eventGuests?.length > 0 && <section className="ev-sec"><h2 className="ev-h">Featured speakers &amp; guests</h2><div className="detail-guests">{event.eventGuests.map(({ guest }: any) => <div key={guest.id}><Avatar src={guest.image} name={`${guest.firstName} ${guest.lastName}`} /><b>{guest.firstName} {guest.lastName}</b>{guest.role && <small>{guest.role}</small>}</div>)}</div></section>}
            {event.sponsors?.length > 0 && <section className="ev-sec"><h2 className="ev-h">Supported by</h2><div className="detail-sponsors">{event.sponsors.map((sponsor: any) => <figure key={sponsor.id}><img src={sponsor.image || imageFallback} alt={sponsor.name} /><figcaption>{sponsor.name}</figcaption></figure>)}</div></section>}
            <section className="ev-sec"><h2 className="ev-h"><MapPin />Location</h2><div className="ev-loc"><div className="ev-loc__text"><b>{event.location?.name || "Location to be announced"}</b><span>{event.location?.address}</span></div><a className="btn btn--ghost btn--sm" href={mapUrl} target="_blank" rel="noreferrer"><Navigation />Get directions</a></div><div className="ev-map"><iframe title={`Map showing ${event.location?.name || event.name}`} src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`} loading="lazy" /></div></section>
          </div>
          <aside className="ev-aside"><div className="ev-book"><div className="ev-book__price"><small>{lowestTicket !== null ? "Tickets from" : "Event access"}</small><b>{ticketPrice}</b></div>{ticketButton("btn btn--primary btn--lg ev-book__cta")}<div className="ev-book__when"><CalendarDays /><div><b>{date(event.startDate)}</b><span>{time(event.startDate)}</span></div></div><p className="detail-note">{ticketDestination ? "Continue to the event's ticket page to book your place." : "Discover more and join the community in the app."}</p></div></aside>
        </div>
        <DownloadAppCard />
      </div>
    </main>
    <div className="ev-bar"><div className="ev-bar__price"><small>{lowestTicket !== null ? "Tickets from" : "Event access"}</small><b>{ticketPrice}</b></div>{ticketButton("btn btn--primary btn--sm")}</div>
  </PublicShell>;
}
